import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bell } from "lucide-react";

// Polls for reminders and fires a toast + browser notification when one is due.
const RemindersCtx = createContext(null);
export const useReminders = () => useContext(RemindersCtx);

const POLL_MS = 5000; // 5s — fine-grained so short offsets like "in 10 seconds" work

export function RemindersProvider({ children }) {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [rs, ms] = await Promise.all([endpoints.reminders(), endpoints.meetings()]);
      setReminders(rs);
      setMeetings(ms);
      setLoaded(true);
      return rs;
    } catch (e) { setLoaded(true); return []; }
  }, []);

  const requestNotifPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "denied";
    if (Notification.permission === "default") {
      const p = await Notification.requestPermission();
      setNotifPermission(p);
      return p;
    }
    return Notification.permission;
  }, []);

  // Fire notifications for due reminders
  const fireDue = useCallback(async () => {
    const list = await refresh();
    const now = Date.now();
    for (const r of list) {
      if (r.completed || r.notified) continue;
      const t = Date.parse(r.remind_at);
      if (isFinite(t) && t <= now) {
        // Show a persistent-ish toast + browser notif
        toast(r.title, {
          description: r.notes || "Reminder is due",
          icon: <Bell size={16} />,
          duration: 15000,
          action: { label: "Mark done", onClick: () => markDone(r.id) },
        });
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try { new Notification("Reminder · " + r.title, { body: r.notes || "" }); } catch (e) { /* ignore */ }
        }
        try { await endpoints.updateReminder(r.id, { notified: true }); } catch (e) { /* ignore */ }
      }
    }
  }, [refresh]);

  const markDone = useCallback(async (id) => {
    await endpoints.updateReminder(id, { completed: true });
    refresh();
  }, [refresh]);

  const createReminder = useCallback(async ({ title, notes, remind_at, project_id, task_id }) => {
    const r = await endpoints.createReminder({ title, notes, remind_at, project_id, task_id });
    refresh();
    return r;
  }, [refresh]);

  const deleteReminder = useCallback(async (id) => {
    await endpoints.deleteReminder(id);
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user || user === false) { setReminders([]); setMeetings([]); clearInterval(timerRef.current); return; }
    fireDue();
    timerRef.current = setInterval(fireDue, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [user, fireDue]);

  return (
    <RemindersCtx.Provider value={{
      reminders, meetings, loaded, refresh, markDone, createReminder, deleteReminder,
      notifPermission, requestNotifPermission,
    }}>
      {children}
    </RemindersCtx.Provider>
  );
}
